// Generat automat din README.md
import React, { useState } from 'react';
import { Target, TrendingUp, BarChart3, Globe, Users, Zap, Eye, Brain, RefreshCw, Download, AlertTriangle, CheckCircle, Clock, ArrowUp, ArrowDown, DollarSign } from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';

const AdaptareLaPiata = () => {
  const [selectedMarket, setSelectedMarket] = useState('romania');
  const [selectedTimeframe, setSelectedTimeframe] = useState('30d');
  const [showInsightModal, setShowInsightModal] = useState(false);
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Date pentru analiza de piață
  const marketData = {
    markets: {
      romania: {
        name: 'România',
        size: '€2.1B',
        growth: 12.5,
        competition: 'Medie',
        opportunities: 8,
        threats: 3,
        marketShare: 3.2,
        customerSentiment: 78,
        trends: [
          { trend: 'Digitalizare accelerată', impact: 'Înalt', probability: 95 },
          { trend: 'Sustenabilitate', impact: 'Mediu', probability: 87 },
          { trend: 'Remote work', impact: 'Înalt', probability: 92 },
          { trend: 'AI & Automation', impact: 'Foarte Înalt', probability: 88 }
        ]
      }
    },

    insights: [
      {
        id: 1,
        type: 'Oportunitate',
        priority: 'Înaltă',
        title: 'Creștere cerere pentru soluții cloud în România',
        description: 'Piața cloud computing în România va crește cu 25% în următorii 6 luni',
        impact: 'Revenue potențial: €450K',
        confidence: 89,
        recommendations: [
          'Dezvoltă pachet de servicii cloud personalizate',
          'Creează campanie de educație despre beneficiile cloud',
          'Parteneriate cu provideri de infrastructură'
        ],
        timeframe: '3-6 luni',
        investmentNeeded: '€75K',
        expectedROI: '600%'
      },
      {
        id: 2,
        type: 'Amenințare',
        priority: 'Medie',
        title: 'Nou competitor pe piața bulgară',
        description: 'TechGiant SA a intrat pe piața bulgară cu preturi cu 20% mai mici',
        impact: 'Risc pierdere: €120K annual',
        confidence: 76,
        recommendations: [
          'Analizează propunerea de valoare diferențiată',
          'Îmbunătățește relațiile cu clienții existenți',
          'Consideră ajustări de preț strategice'
        ],
        timeframe: '1-2 luni',
        investmentNeeded: '€30K',
        expectedROI: '200%'
      }
    ],

    competitors: [
      {
        name: 'TechLeader Pro',
        marketShare: 15.2,
        strengths: ['Brand recunoscut', 'Portofoliu complet'],
        weaknesses: ['Prețuri mari', 'Inovație lentă'],
        recentMoves: 'Achiziție startup AI în ianuarie',
        threat: 'Mediu'
      },
      {
        name: 'Digital Solutions Hub',
        marketShare: 12.8,
        strengths: ['Preturi competitive', 'Echipă tânără'],
        weaknesses: ['Experiență limitată', 'Referințe puține'],
        recentMoves: 'Extindere în Bulgaria',
        threat: 'Înalt'
      }
    ],

    adaptiveStrategies: [
      {
        id: 1,
        title: 'Diversificare Geografică',
        description: 'Extindere rapidă în Bulgaria pentru a profita de concurența scăzută',
        status: 'În implementare',
        progress: 65,
        budget: '€120K',
        timeline: '4 luni'
      },
      {
        id: 2,
        title: 'Repoziționare ca AI-First Company',
        description: 'Transformarea în companie cu focus pe soluții de inteligență artificială',
        status: 'Planificat',
        progress: 15,
        budget: '€200K',
        timeline: '8 luni'
      }
    ]
  };

  // Statistici generale
  const adaptationStats = {
    totalOpportunities: marketData.insights.filter(i => i.type === 'Oportunitate').length,
    activeThreats: marketData.insights.filter(i => i.type === 'Amenințare').length,
    adaptationScore: 78.5,
    marketResponsiveness: 84,
    competitiveAdvantage: 67,
    strategiesActive: marketData.adaptiveStrategies.filter(s => s.status === 'În implementare').length
  };

  const markets = [
    { value: 'romania', label: 'România' },
    { value: 'all', label: 'Toate piețele' }
  ];

  const timeframes = [
    { value: '7d', label: '7 zile' },
    { value: '30d', label: '30 zile' },
    { value: '90d', label: '3 luni' },
    { value: '1y', label: '1 an' }
  ];

  const getImpactColor = (impact) => {
    const colors = {
      'Foarte Înalt': 'error',
      'Înalt': 'warning',
      'Mediu': 'info',
      'Scăzut': 'success'
    };
    return colors[impact] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Înaltă': 'error',
      'Medie': 'warning',
      'Scăzută': 'success'
    };
    return colors[priority] || 'default';
  };

  const getThreatColor = (threat) => {
    const colors = {
      'Înalt': 'error',
      'Mediu': 'warning',
      'Scăzut': 'success'
    };
    return colors[threat] || 'default';
  };

  const getTypeIcon = (type) => {
    const icons = {
      'Oportunitate': TrendingUp,
      'Amenințare': AlertTriangle,
      'Trend': BarChart3,
      'Regulament': CheckCircle
    };
    return icons[type] || Target;
  };

  const getStatusColor = (status) => {
    const colors = {
      'În implementare': 'success',
      'Planificat': 'warning',
      'Cercetare': 'info',
      'Finalizat': 'success'
    };
    return colors[status] || 'default';
  };

  const handleInsightClick = (insight) => {
    setSelectedInsight(insight);
    setShowInsightModal(true);
  };

  const handleAnalyze = () => {
    setIsAnalyzing(true);
    setTimeout(() => {
      setIsAnalyzing(false);
    }, 3000);
  };

  const currentMarket = selectedMarket !== 'all' ? marketData.markets[selectedMarket] : null;

  const insightsColumns = [
    { 
      header: 'Tip', 
      key: 'type',
      render: (value) => {
        const Icon = getTypeIcon(value);
        return (
          <div className="flex items-center space-x-2">
            <Icon className="w-4 h-4" />
            <span>{value}</span>
          </div>
        );
      }
    },
    { 
      header: 'Prioritate', 
      key: 'priority',
      render: (value) => <Badge variant={getPriorityColor(value)}>{value}</Badge>
    },
    { 
      header: 'Titlu', 
      key: 'title',
      render: (value) => (
        <div className="max-w-64 truncate" title={value}>
          {value}
        </div>
      )
    },
    { 
      header: 'Impact', 
      key: 'impact',
      render: (value) => (
        <div className="text-sm font-medium text-gray-900 dark:text-white">
          {value}
        </div>
      )
    },
    { 
      header: 'Încredere', 
      key: 'confidence',
      render: (value) => (
        <div className="flex items-center space-x-2">
          <div className="w-16 bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${value >= 85 ? 'bg-green-500' : value >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`}
              style={{ width: `${value}%` }}
            ></div>
          </div>
          <span className="text-sm font-medium">{value}%</span>
        </div>
      )
    },
    { header: 'Timeframe', key: 'timeframe' }
  ];

  const competitorsColumns = [
    { header: 'Competitor', key: 'name' },
    { 
      header: 'Market Share', 
      key: 'marketShare',
      render: (value) => `${value}%`
    },
    { 
      header: 'Threat Level', 
      key: 'threat',
      render: (value) => <Badge variant={getThreatColor(value)}>{value}</Badge>
    },
    { 
      header: 'Recent Activity', 
      key: 'recentMoves',
      render: (value) => (
        <div className="max-w-48 truncate" title={value}>
          {value}
        </div>
      )
    }
  ];

  const strategiesColumns = [
    { header: 'Strategie', key: 'title' },
    { 
      header: 'Status', 
      key: 'status',
      render: (value) => <Badge variant={getStatusColor(value)}>{value}</Badge>
    },
    { 
      header: 'Progres', 
      key: 'progress',
      render: (value) => (
        <div className="flex items-center space-x-2">
          <div className="w-16 bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${
                value >= 80 ? 'bg-green-500' : value >= 50 ? 'bg-yellow-500' : 'bg-blue-500'
              }`}
              style={{ width: `${value}%` }}
            ></div>
          </div>
          <span className="text-sm font-medium">{value}%</span>
        </div>
      )
    },
    { header: 'Budget', key: 'budget' },
    { header: 'Timeline', key: 'timeline' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Adaptare la Piață</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Analize de piață în timp real cu recomandări pentru strategii adaptive
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={handleAnalyze}
            disabled={isAnalyzing}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isAnalyzing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Analizează...</span>
              </>
            ) : (
              <>
                <Brain className="w-4 h-4" />
                <span>Analiză AI</span>
              </>
            )}
          </button>

          <button className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
            <Download className="w-4 h-4" />
            <span>Export Raport</span>
          </button>
        </div>
      </div>

      {/* Statistici generale */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <Card>
          <div className="flex items-center space-x-3">
            <TrendingUp className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{adaptationStats.totalOpportunities}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Oportunități</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-8 h-8 text-red-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{adaptationStats.activeThreats}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Amenințări</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-3">
            <Target className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{adaptationStats.adaptationScore}%</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Scor Adaptare</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-3">
            <Zap className="w-8 h-8 text-yellow-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{adaptationStats.marketResponsiveness}%</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Reactivitate</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-3">
            <BarChart3 className="w-8 h-8 text-purple-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{adaptationStats.competitiveAdvantage}%</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Avantaj Comp.</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-8 h-8 text-orange-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{adaptationStats.strategiesActive}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Strategii Active</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Controale filtrare */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Piața</label>
            <select
              value={selectedMarket}
              onChange={(e) => setSelectedMarket(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            >
              {markets.map(market => (
                <option key={market.value} value={market.value}>
                  {market.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Perioada</label>
            <select
              value={selectedTimeframe}
              onChange={(e) => setSelectedTimeframe(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            >
              {timeframes.map(timeframe => (
                <option key={timeframe.value} value={timeframe.value}>
                  {timeframe.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Analiza pieței selectate */}
      {currentMarket && (
        <Card title={`Analiza Pieței - ${currentMarket.name}`} icon={Globe}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">Overview Piață</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-sm text-blue-600 dark:text-blue-400">Mărime Piață</p>
                  <p className="text-2xl font-bold text-blue-800 dark:text-blue-300">{currentMarket.size}</p>
                </div>
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <p className="text-sm text-green-600 dark:text-green-400">Creștere</p>
                  <p className="text-2xl font-bold text-green-800 dark:text-green-300">{currentMarket.growth}%</p>
                </div>
                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <p className="text-sm text-purple-600 dark:text-purple-400">Market Share</p>
                  <p className="text-2xl font-bold text-purple-800 dark:text-purple-300">{currentMarket.marketShare}%</p>
                </div>
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <p className="text-sm text-yellow-600 dark:text-yellow-400">Sentiment</p>
                  <p className="text-2xl font-bold text-yellow-800 dark:text-yellow-300">{currentMarket.customerSentiment}%</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">Trending Topics</h3>
              <div className="space-y-3">
                {currentMarket.trends.map((trend, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{trend.trend}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <Badge variant={getImpactColor(trend.impact)} size="sm">{trend.impact}</Badge>
                        <span className="text-sm text-gray-600 dark:text-gray-400">{trend.probability}% probabilitate</span>
                      </div>
                    </div>
                    {trend.impact === 'Foarte Înalt' || trend.impact === 'Înalt' ? (
                      <ArrowUp className="w-5 h-5 text-green-600" />
                    ) : (
                      <ArrowDown className="w-5 h-5 text-yellow-600" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Insights strategice */}
      <Card title="Insights Strategice" icon={Brain}>
        <Table
          columns={insightsColumns}
          data={marketData.insights}
          onRowClick={(row) => handleInsightClick(row)}
        />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Analiza competitorilor */}
        <Card title="Analiza Competitorilor" icon={Target}>
          <Table columns={competitorsColumns} data={marketData.competitors} />
        </Card>

        {/* Strategii adaptive */}
        <Card title="Strategii Adaptive" icon={Zap}>
          <Table columns={strategiesColumns} data={marketData.adaptiveStrategies} />
        </Card>
      </div>

      {/* Modal detalii insight */}
      {showInsightModal && selectedInsight && (
        <Modal
          isOpen={showInsightModal}
          onClose={() => setShowInsightModal(false)}
          title={selectedInsight.title}
        >
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {(() => {
                  const Icon = getTypeIcon(selectedInsight.type);
                  return <Icon className="w-5 h-5" />;
                })()}
                <span className="font-semibold">{selectedInsight.type}</span>
              </div>
              <Badge variant={getPriorityColor(selectedInsight.priority)} className="text-lg p-2">
                Prioritate {selectedInsight.priority}
              </Badge>
            </div>

            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-gray-700 dark:text-gray-300">{selectedInsight.description}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <h4 className="font-semibold text-blue-800 dark:text-blue-400 mb-2">Impact Financiar</h4>
                <p className="text-blue-600 dark:text-blue-300">{selectedInsight.impact}</p>
              </div>
              
              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <h4 className="font-semibold text-purple-800 dark:text-purple-400 mb-2">ROI Așteptat</h4>
                <p className="text-purple-600 dark:text-purple-300">{selectedInsight.expectedROI}</p>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Recomandări de Acțiune</h4>
              <div className="space-y-2">
                {selectedInsight.recommendations.map((rec, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-green-800 dark:text-green-400">{rec}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t dark:border-gray-700">
              <button
                onClick={() => setShowInsightModal(false)}
                className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Închide
              </button>
              <button className="px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 rounded-lg transition-colors">
                Implementează
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AdaptareLaPiata;